/**
 * Sneakers, saying something.
 *
 * The mascot has existed in the mockups and the character bible since the first
 * week ([Mascot](../../../docs/MASCOT.md)) and has never once appeared in the
 * app. The lines he says have existed for nearly as long: authored in
 * `content/en-da/narration.json`, checked by the linter, seeded into MySQL, and
 * read by nothing. This is the component that finally says them.
 *
 * **He speaks by himself.** A line arrives and is spoken, once, without a tap —
 * a child who cannot yet read the sentence still hears it, which is the whole
 * point of having a mascot rather than a paragraph. Tapping him says it again
 * ([D-064](../../../docs/DECISIONS.md#d-064) decides *how*: the recording if the
 * course has one, the device's English voice if not, and silence handled rather
 * than pretended).
 *
 * **He is never a still picture** ([D-018](../../../docs/DECISIONS.md#d-018),
 * [D-139](../../../docs/DECISIONS.md#d-139)). He is drawn inline, from the same
 * committed SVG as before, so its named parts can move: he breathes, blinks
 * every three to six seconds, glances around now and then, his tassel sways,
 * and his head bobs while he talks. His mouth moves to what he says
 * ([D-081](../../../docs/DECISIONS.md#d-081)). A child who asked for less
 * movement, or whose device did, gets a Sneakers who holds still.
 */

import drawing from '@capytalk/tokens/sneakers.svg?raw';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BLINK_EVERY_MS,
  BLINK_MS,
  type Glance,
  LOOK_EVERY_MS,
  LOOK_MS,
  speak as speakVisemes,
  nextGlance,
  waitBetween,
} from '@capytalk/core';
import { hush, say, whileSpeaking } from './audio.js';

/**
 * Sneakers is the app talking, and the app talks English in every course
 * ([D-068](../../../docs/DECISIONS.md#d-068)): a line with no recording is read
 * by an English voice even while a child is learning Swedish (D-195).
 */
const APP_TALK = { language: 'en' } as const;
import Mouth from './Mouth.js';
import { phonemesOf } from './phonemes.js';
import { Tappable } from './Hint.js';
import { activePrefs, holdStill } from './prefs.js';

/**
 * The drawing, as decoration. The file describes itself as an image for anyone
 * who opens it on its own; here it sits inside a button that already says what
 * it does, and a second name would be read out twice.
 */
const SNEAKERS = drawing
  .replace(/\srole="img"/, ' aria-hidden="true" focusable="false"')
  .replace(/\saria-label="[^"]*"/, '');

/**
 * Breathing is CSS; blinking and glancing happen at random moments, so they are
 * switched on from here. Nothing is scheduled at all when he should hold still.
 */
function useIdleLife(): { readonly blinking: boolean; readonly look: Glance | null } {
  const [blinking, setBlinking] = useState(false);
  const [look, setLook] = useState<Glance | null>(null);

  useEffect(() => {
    if (holdStill()) return undefined;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, then: () => void) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        then();
      }, ms);
      timers.add(timer);
    };
    const blink = () => {
      later(waitBetween(BLINK_EVERY_MS, Math.random()), () => {
        setBlinking(true);
        later(BLINK_MS, () => {
          setBlinking(false);
          blink();
        });
      });
    };
    let last: Glance | null = null;
    const glance = () => {
      later(waitBetween(LOOK_EVERY_MS, Math.random()), () => {
        last = nextGlance(last, Math.random());
        setLook(last);
        later(LOOK_MS, () => {
          setLook(null);
          glance();
        });
      });
    };
    blink();
    glance();
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, []);

  return { blinking, look };
}

export default function Sneakers({
  text,
  audioRef,
  /** How big he is. The mockups use 56 beside a bubble and 200 on his own. */
  size = 56,
  /**
   * Say it on arrival.
   *
   * On by default, because a line that has to be tapped for is a line most
   * children never hear. Off where he is only illustrating something the child
   * is already reading.
   */
  speak = true,
}: {
  readonly text: string;
  /** The recording to prefer. Null when the course has none for this line. */
  readonly audioRef: string | null;
  readonly size?: number;
  readonly speak?: boolean;
}) {
  const [silent, setSilent] = useState(false);
  const { blinking, look } = useIdleLife();
  // A bob while he talks. Any voice counts: two of him on one screen is rare,
  // and both nodding along is not wrong.
  const [talking, setTalking] = useState(false);
  useEffect(
    () =>
      whileSpeaking((at) => {
        setTalking(at !== null);
      }),
    [],
  );
  /**
   * His mouth for this line, from the phonemes of the words in it.
   *
   * Worked out here rather than asked for: the words the course teaches carry
   * their phonemes already, and anything else moves his mouth roughly rather
   * than not at all.
   */
  const mouth = useMemo(() => speakVisemes(phonemesOf(text)), [text]);
  // Whether *this* line has been spoken, so a re-render does not restart it
  // mid-sentence. Keyed by the text: a new line is a new thing to say.
  const said = useRef<string | null>(null);

  useEffect(() => {
    // A child who turned his voice off still gets the line on screen, and a
    // tap on him still plays it: off means *not unasked*, not never (D-133).
    if (!speak || !activePrefs().sneakersTalks || said.current === text) return;
    said.current = text;
    void say(audioRef, text, APP_TALK).then((how) => {
      setSilent(how.from === 'none');
    });
  }, [text, audioRef, speak]);

  // Stop mid-sentence when he goes away, or he talks over the first question.
  useEffect(() => hush, []);

  const again = () => {
    said.current = text;
    void say(audioRef, text, APP_TALK).then((how) => {
      setSilent(how.from === 'none');
    });
  };

  return (
    <div className="sn-row">
      {/*
        A button rather than a picture with a handler: tapping him replays the
        line, so it is a control, and a control a screen reader cannot find is
        one a child using VoiceOver does not have.
      */}
      <button
        type="button"
        aria-label="Hear it again"
        style={{ background: 'none', border: 0, padding: 0, flex: '0 0 auto' }}
        onClick={again}
      >
        {/*
          The mouth is drawn over him, so it moves while he talks. Relative,
          because the overlay is positioned against this box
          ([D-081](../../../docs/DECISIONS.md#d-081)).
        */}
        <span
          className={['sn-rig', blinking ? 'blink' : '', talking ? 'talking' : '']
            .filter(Boolean)
            .join(' ')}
          data-look={look ?? undefined}
          style={
            {
              width: size,
              // For the mouth overlay, which bobs with the head in screen pixels.
              '--sn-scale': String(size / 240),
            } as React.CSSProperties
          }
        >
          {/* Local, committed, CI-checked markup — never anything a response chose. */}
          <span dangerouslySetInnerHTML={{ __html: SNEAKERS }} />
          <span className="sn-mouth">
            <Mouth track={mouth} size={size} />
          </span>
        </span>
      </button>
      <div className="bubble">
        {/*
          `role="status"` rather than `alert`: it is worth announcing and it is
          never urgent. A mascot's hello does not interrupt.
        */}
        {/*
          His lines are English too, and they are the first English a child
          meets in a lesson — so the words in them are askable like any other.
        */}
        <span role="status">
          <Tappable text={text} />
        </span>
        {/*
          A repeat button you can see. Tapping Sneakers himself always replayed
          the line, but nothing said so — a child who missed the sentence had
          no visible way to hear it again.
        */}
        {!silent && (
          <button
            type="button"
            className="spk"
            aria-label="Say it again"
            title="Say it again"
            onClick={again}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
              opacity: 0.8,
              minWidth: 32,
              minHeight: 32,
            }}
          >
            <span aria-hidden="true">🔁</span>
          </button>
        )}
      </div>
    </div>
  );
}
